import { Typography } from 'antd'
import isNil from 'lodash/isNil'
import { WeldIndication } from 'pages/reporting/reports/raut/hooks/useWeldIndications'
import { WeldScanFiles } from 'pages/reporting/reports/raut/hooks/useWeldScanFiles'
import { useUnitSystem } from 'pages/reporting/shared/contexts/UnitSystemContext'
import React, { useRef } from 'react'
import styled from 'styled-components'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import { WeldSideViewPlot, WeldTopViewPlot } from './WeldHeatmapPlot'

const { Text } = Typography

const DisplayContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const SScanContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const SScanLabel = styled(Text)`
  font-size: 11px;
  font-weight: 500;
  margin-bottom: 4px;
`

const SScanImage = styled.img`
  max-width: 560px;
  width: 100%;
  height: auto;
  border-radius: 4px;
  background: white;
`

const ProjectionsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
`

const ProjectionContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const GraphsContainer = styled.div.attrs({
  className: 'weld-graphs-container',
})`
  padding: 8px;
  border-radius: 4px;
`

const StatsContainer = styled.div`
  margin-top: 8px;
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
`

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`

const NoDataMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 150px;
  font-size: 12px;
  border-radius: 4px;
  opacity: 0.6;
`

interface WeldIndicationDisplayProps {
  /** Scan files for this indication or file */
  scanFiles: WeldScanFiles
  /** The indication data (optional - not present for files without indications) */
  indication?: WeldIndication
  /** File name (used when no indication is present) */
  fileName?: string
  /** Reference for screenshot capture */
  graphsRef?: React.RefObject<HTMLDivElement>
  /** Whether scan data is loading */
  isLoading?: boolean
}

/**
 * WeldIndicationDisplay - Main visualization component for weld indications
 *
 * Layout:
 * - Top row: S-scan Left | S-scan Overlay | S-scan Right
 * - Middle: Top View - cross-weld vs along-weld
 * - Bottom: Side View - depth vs along-weld
 * - Stats: Z position, gain (dB), max amplitude, and other indication metadata
 */
export const WeldIndicationDisplay: React.FC<WeldIndicationDisplayProps> = ({
  scanFiles,
  indication,
  graphsRef,
  isLoading = false,
}) => {
  const localRef = useRef<HTMLDivElement>(null)
  const containerRef = graphsRef ?? localRef
  const { getGeometricUnit, measurementPrecision } = useUnitSystem()
  const geometricUnit = getGeometricUnit()

  const { sscanImageUrl, topView, sideView } = scanFiles

  // Calculate dimensions for projection views
  const projectionWidth = 560
  const topViewHeight = 100
  const sideViewHeight = 120

  const zIndex = indication?.customInfo?.z_index

  if (isLoading) {
    return (
      <DisplayContainer>
        <NoDataMessage>Loading scan data...</NoDataMessage>
      </DisplayContainer>
    )
  }

  const hasSScanData = !!sscanImageUrl
  const hasProjectionData = topView || sideView

  if (!hasSScanData && !hasProjectionData) {
    return (
      <DisplayContainer>
        <NoDataMessage>
          No scan data available for this indication
        </NoDataMessage>
      </DisplayContainer>
    )
  }

  // Format Z position for display using the current unit system
  const formatZPosition = () => {
    if (!indication) return 'N/A'

    const zAtMax = indication.customInfo?.z_at_max
    const location = indication.customInfo?.location

    if (!isNil(zAtMax)) {
      const measurement = GeckoMeasurement.CreateFromValue(
        zAtMax,
        GeckoMeasurementUnit.Meters
      )
      return `${measurement.toNumber(geometricUnit).toFixed(measurementPrecision)} ${geometricUnit.id}`
    }
    if (!isNil(location?.startZ) && !isNil(location?.endZ)) {
      const midZ = (location.startZ + location.endZ) / 2
      const measurement = GeckoMeasurement.CreateFromValue(
        midZ,
        GeckoMeasurementUnit.Meters
      )
      return `${measurement.toNumber(geometricUnit).toFixed(measurementPrecision)} ${geometricUnit.id}`
    }
    return 'N/A'
  }

  return (
    <DisplayContainer>
      <GraphsContainer ref={containerRef}>
        {/* S-Scan composite image from paut-display (contains Left | Merged | Right) */}
        {hasSScanData && (
          <SScanContainer>
            <SScanLabel>S-Scan Views (Left | Merged | Right)</SScanLabel>
            <SScanImage
              src={sscanImageUrl}
              alt='S-scan composite (Left, Merged, Right)'
            />
          </SScanContainer>
        )}

        {/* Projection Views: Top view and Side view */}
        {hasProjectionData && (
          <ProjectionsRow>
            <ProjectionContainer>
              {topView ? (
                <WeldTopViewPlot
                  data={topView}
                  width={projectionWidth}
                  height={topViewHeight}
                  isStatic={true}
                  zIndex={zIndex}
                  showCrosshair={zIndex !== undefined}
                />
              ) : (
                <NoDataMessage style={{ height: topViewHeight }}>
                  No top view data
                </NoDataMessage>
              )}
            </ProjectionContainer>

            <ProjectionContainer>
              {sideView ? (
                <WeldSideViewPlot
                  data={sideView}
                  width={projectionWidth}
                  height={sideViewHeight}
                  isStatic={true}
                  zIndex={zIndex}
                  showCrosshair={zIndex !== undefined}
                />
              ) : (
                <NoDataMessage style={{ height: sideViewHeight }}>
                  No side view data
                </NoDataMessage>
              )}
            </ProjectionContainer>
          </ProjectionsRow>
        )}
      </GraphsContainer>

      {/* Stats Section - Only show for indications */}
      {indication && (
        <StatsContainer>
          <StatRow>
            <Text strong>Indication:</Text>
            <Text>{indication.title}</Text>
          </StatRow>
          <StatRow>
            <Text strong>Z Position (Along-Weld):</Text>
            <Text>{formatZPosition()}</Text>
          </StatRow>
          {indication.customInfo?.max_amplitude !== undefined && (
            <StatRow>
              <Text strong>Max Amplitude:</Text>
              <Text>{indication.customInfo.max_amplitude.toFixed(1)}%</Text>
            </StatRow>
          )}
          {indication.customInfo?.acceptance && (
            <StatRow>
              <Text strong>Acceptance:</Text>
              <Text>{indication.customInfo.acceptance}</Text>
            </StatRow>
          )}
          {indication.customInfo?.damage_mechanism && (
            <StatRow>
              <Text strong>Type:</Text>
              <Text>{indication.customInfo.damage_mechanism}</Text>
            </StatRow>
          )}
        </StatsContainer>
      )}
    </DisplayContainer>
  )
}

export default WeldIndicationDisplay
